import React, { FC, useEffect, useMemo, useState } from 'react';
import sum from 'lodash/sum';

import Details from './Scenes/Details';
import Upsells from './Scenes/Upsells';
import type { CompleteExpressQueryKiosk_company_categories_items_upsales_items } from '../../Components/useExpressQuery/__queries__';
import type { LocalItem, LocalVariation, TranslatedString } from '~Types';
import { useEntryFinder } from '~Components/EntryFinder';
import useItemQuantity from '~Scenes/SelfServiceKiosks/Hooks/useItemQuantity';
import { SCENES } from '../General/index';
import { maxItemsQuantity } from '../General';
import { useI18n } from '~Hooks';
import useItemModifications from '~Hooks/ItemModifications/useItemModifications';

export type Order = {
  item: LocalItem;
  selectedVariations: LocalVariation[];
  removedVariations: LocalVariation[];
  quantity: number;
  price: number;
};
export type Choice = {
  id: string;
  name: string;
  price: number;
};
export type SelectedUpsell = Choice & {
  currentStep: number;
};
export type SelectedUpsells = {
  [key: string]: Choice[];
};
export interface Items extends CompleteExpressQueryKiosk_company_categories_items_upsales_items {
  price: number;
}
export interface LocalUpsell {
  id: string;
  name: TranslatedString;
  hideInEPOS: boolean;
  enableMultiSelect: boolean;
  items: Items[];
}
export interface Upsell {
  id: string;
  name: TranslatedString;
  items: CompleteExpressQueryKiosk_company_categories_items_upsales_items[];
}

type Props = {
  cartItemsQuantity: number;
  cartItems: LocalItem[];
  item: LocalItem;
  onAddToOrder: (
    id: string,
    variations: LocalVariation[],
    removedVariations: LocalVariation[],
    count: number,
    hasUpsell: boolean,
  ) => { cartItemIds: string[] };
  onAddItemUpsellToOrder: (upsellItemId: string, parentCartId: string) => void;
  onClose: () => void;
  itemScene: SCENES;
  setItemScene: React.Dispatch<React.SetStateAction<SCENES>>;
};

const Item: FC<Props> = ({
  cartItemsQuantity,
  cartItems,
  item,
  onAddToOrder,
  onAddItemUpsellToOrder,
  onClose,
  itemScene,
  setItemScene,
}) => {
  const findEntryFromItem = useEntryFinder();
  const itemModifications = useItemModifications(item?.modificationGroups ?? []);

  const [price, setPrice] = useState<number>(0);
  const [selectedUpsells, setSelectedUpsells] = useState<SelectedUpsells>({});
  const [order, setOrder] = useState<Order>({
    item,
    selectedVariations: [],
    removedVariations: [],
    quantity: 0,
    price,
  });

  const choicesArr: Choice[] = Object.values(selectedUpsells).flatMap((choices) => choices);
  const upsellsQuantity = choicesArr.length;
  const { quantity, onChangeQuantity } = useItemQuantity({
    id: item.id,
    cartItems,
    upsellsQuantity,
  });
  const entry = findEntryFromItem(item.id);

  const amount = useMemo(
    () => sum([entry?.price, ...itemModifications.selectedVariations.map((sv) => sv.price)]) * quantity,
    [quantity, entry, itemModifications.selectedVariations],
  );

  useEffect(() => {
    const upsalesPrice = choicesArr.reduce((acc, cur) => {
      const foundItem = findEntryFromItem(cur.id);
      foundItem && (acc += foundItem.price);
      return acc;
    }, 0);

    setPrice(amount ? amount + upsalesPrice : 0);
  }, [choicesArr, amount]);

  useEffect(() => {
    setItemScene(SCENES.DETAILS);
  }, []);

  // This place needs to be changed in the future, types do not match
  const upsells: /*LocalUpsell[] |*/ any[] = useMemo(() => {
    if (!item?.upsales) {
      return [];
    }

    return item.upsales
      .filter((u) => !u.hideInEPOS)
      .map((upsell) => ({
        ...upsell,
        items: upsell.items
          .map((i) => {
            const findEntry = findEntryFromItem(i.id);
            return findEntry && { ...findEntry, ...i, upsale: true };
          })
          .filter(Boolean),
      }))
      .filter((upsell) => upsell.items.length > 0);
  }, [item, findEntryFromItem]);

  const handleChangeScene = (changeScene: SCENES) => {
    setItemScene(changeScene);
  };

  const onComplete = () => {
    const hasUpsales = Boolean(choicesArr.length > 0);
    const { cartItemIds } = onAddToOrder(
      item.id,
      itemModifications.selectedVariations,
      itemModifications.removedVariations,
      quantity,
      hasUpsales,
    );

    choicesArr?.map((i) => {
      onAddItemUpsellToOrder(i.id, cartItemIds[0]);
    });
    setItemScene(SCENES.DETAILS);
    onClose();
  };

  const onContinue = () => {
    if (upsells.length) {
      handleChangeScene(SCENES.UPSELLS);
      setOrder({
        item,
        selectedVariations: itemModifications.selectedVariations,
        removedVariations: itemModifications.removedVariations,
        quantity,
        price,
      });
    } else {
      onComplete();
    }
  };

  /* This function handles when we remove or add upsellItems to the Basket */
  const handleSetChoice = (
    currentStep: number,
    choiceOrId: SelectedUpsell | string, // The choice object or id to perform the operation on
    enableMultiSelect: boolean,
  ) => {
    setSelectedUpsells((prev) => {
      const currentChoices = prev[`step-${currentStep}`] || [];

      /* If it's a string, it's an id to delete, remove only the first occurrence */
      if (typeof choiceOrId === 'string') {
        const indexToRemove = currentChoices.findIndex((choice) => choice.id === choiceOrId);

        /* If an occurrence is found, remove it */
        if (indexToRemove !== -1) {
          const updatedChoices = [...currentChoices];
          updatedChoices.splice(indexToRemove, 1); // Remove the first occurrence

          return {
            ...prev,
            [`step-${currentStep}`]: updatedChoices.length > 0 ? updatedChoices : [], // Handle empty array
          };
        }
      } else {
        /* If it's a SelectedUpsell object, add the choice */
        return {
          ...prev,
          [`step-${currentStep}`]: enableMultiSelect
            ? [...currentChoices, choiceOrId]
            : [choiceOrId],
        };
      }

      return prev;
    });
  };

  if (itemScene === SCENES.DETAILS) {
    return (
      <Details
        cartItemsQuantity={cartItemsQuantity}
        modificationGroups={itemModifications.modificationGroups}
        onSelectVariation={itemModifications.onSelectVariation}
        quantity={quantity}
        onChangeQuantity={onChangeQuantity}
        item={item}
        price={price}
        onSubmit={onContinue}
      />
    );
  } else if (itemScene === SCENES.UPSELLS) {
    return (
      <Upsells
        cartItems={cartItems}
        disabled={cartItemsQuantity + quantity + upsellsQuantity >= maxItemsQuantity}
        upsells={upsells}
        toDetailScreen={() => handleChangeScene(SCENES.DETAILS)}
        choicesArr={choicesArr}
        onSubmit={onComplete}
        order={order}
        handleSetChoice={handleSetChoice}
        price={price}
      />
    );
  }
  return null;
};

export default Item;
